import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { writeStoredActiveBrandKitId } from '@/components/create/v2/utils/brandKitPersistence';
import { queryKeys } from '@/config/queryClient';
import { useOptionalCreateSettings } from '@/contexts/CreateSettingsContext';
import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { type BusinessModel, parseBusinessModel } from '@/lib/analytics/business-model';
import {
  type BrandNameCandidate,
  isPlaceholderBrandName,
  pickBrandNameCandidate,
  resolveRenderBrandName,
} from '@/lib/brand/brand-name-truth';
import { BRAND_KIT_CAP } from '@/lib/brand/kit-cap';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { detectBrowserTimezone } from '@/lib/scheduling/browser-timezone';
import { readActiveWorkspacePointer } from '@/lib/workspace/activeWorkspacePointer';
import { eligibleWorkspaces, pickActiveWorkspace } from '@/lib/workspace/pickActiveWorkspace';
import { switchWorkspace } from '@/lib/workspace/switchWorkspace';
import { logger } from '@/utils/logger';
import { showErrorToast } from '@/utils/toast';

type BrandKitRow = Database['public']['Tables']['brand_kits']['Row'];

export { BRAND_KIT_CAP };

export const ARCHIVE_OWNER_ONLY_MESSAGE =
  'Only the account owner can archive or restore a workspace.';

export function archiveRpcErrorMessage(error: { code?: string | null; message: string }): string {
  return error.code === '42501' ? ARCHIVE_OWNER_ONLY_MESSAGE : error.message;
}

export function useBrandKitScheduledPostCount(kitId: string | null, enabled = true) {
  const { user } = useAuth();
  return useQuery<number>({
    queryKey: [...queryKeys.brandKits.all, 'scheduled-post-count', kitId],
    enabled: enabled && Boolean(kitId) && Boolean(user?.id),
    staleTime: 0,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('brand_kit_scheduled_post_count', {
        p_kit_id: kitId as string,
      });
      if (error) throw new Error(archiveRpcErrorMessage(error));
      return data ?? 0;
    },
  });
}

/**
 * Narrow structural type covering only the client surface these pure
 * functions touch, so tests can mock it without importing the real
 * SupabaseClient type.
 */
interface BrandKitMutationClient {
  from: (table: string) => {
    update: (payload: Record<string, unknown>) => {
      eq: (
        col: string,
        val: unknown,
      ) => {
        eq: (
          col: string,
          val: unknown,
        ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
      };
    };
    /**
     * Insert WITHOUT a `.select()` — deliberately. See `createBrandKit`: adding
     * a RETURNING clause here makes every workspace creation fail with 42501.
     */
    insert: (payload: Record<string, unknown>) => PromiseLike<{
      error: { message: string } | null;
    }>;
  };
}

/**
 * Sets the ACCOUNT's default workspace — the one every member of the account
 * inherits until they switch for themselves.
 *
 * ⚠️ THIS IS NOT "switch my workspace" any more. It was, until 2026-08-22, and
 * that was the defect: `brand_kits` is account-scoped, so one member switching
 * moved every member. Per-user switching is `switchWorkspace()`
 * (`src/lib/workspace/`), which writes `user_profiles.active_brand_kit_id` and
 * leaves this column alone.
 *
 * Callers should be rare and deliberate: Settings → Brand ("make this the
 * default for the team"), and onboarding picking the first kit. If you are
 * reaching for this from a switcher UI, you want `setActiveKit`.
 *
 * Copies the clear-then-set default transaction from save-brand-kit.ts:131-140.
 * Clears is_default on the user's current default kit BEFORE setting it on the
 * target — if the clear fails, the set is never issued. Defense-in-depth
 * `.eq('user_id', userId)` on both legs even under RLS.
 */
export async function setDefaultBrandKit(
  client: BrandKitMutationClient,
  ownerId: string,
  kitId: string,
): Promise<void> {
  const { error: clearErr } = await client
    .from('brand_kits')
    .update({ is_default: false })
    .eq('user_id', ownerId)
    .eq('is_default', true);
  if (clearErr) throw new Error(clearErr.message);

  const { error: setErr } = await client
    .from('brand_kits')
    .update({ is_default: true })
    .eq('id', kitId)
    .eq('user_id', ownerId);
  if (setErr) throw new Error(setErr.message);
}

/**
 * Inserts a new brand kit for the user, auto-defaulting the first kit.
 * Throws (without inserting) once the user is at BRAND_KIT_CAP.
 *
 * `accountId` is REQUIRED as of the 2026-08-23 workspace-tenancy RLS flip
 * (`brand_kits_insert_own_account`, migration
 * `20260823045855_workspace_tenancy_phase5a_brand_kits_rls_flip.sql`): the
 * INSERT policy's WITH CHECK is `account_id IN (SELECT id FROM accounts WHERE
 * owner_user_id = current_account_owner_id())` — a row inserted without
 * `account_id` satisfies no account and is rejected with a bare 403. This
 * function predates that migration by hours and was never updated, which
 * silently broke EVERY brand-kit creation path (first workspace AND
 * additional workspace) the moment the migration landed. Caught live via
 * Playwright while verifying the "add a second workspace" fix.
 */
export async function createBrandKit(
  client: BrandKitMutationClient,
  ownerId: string,
  existingCount: number,
  name?: string,
  accountId?: string,
  /**
   * Franchise/enterprise "Add a location": links the new row to its corporate
   * parent (`AddBrandDialog` resolves this to the caller's currently active
   * workspace, normalized to that workspace's own root if it is itself a
   * location — see the dialog's `isLocation` handling). Omitted for an
   * ordinary independent workspace, which is why this stays optional rather
   * than required.
   */
  parentBrandKitId?: string | null,
): Promise<string> {
  if (existingCount >= BRAND_KIT_CAP) {
    throw new Error('Brand kit limit reached');
  }
  if (!accountId) {
    throw new Error('Could not resolve the account for this workspace');
  }

  // A REAL name, when the caller has one.
  //
  // The `'New brand kit'` default is why this function was taken off the UI:
  // it produced rows nobody could tell apart, and the image pipeline once
  // printed that literal string onto a product because a label existed. The
  // fix is not to forbid creation — it is to refuse to invent a name. A caller
  // that asks the user for one is safe; a caller that passes nothing still
  // gets the old placeholder, and still should not be wired to a button.
  const trimmed = name?.trim();

  // 🔴 The id is generated HERE, and the insert carries NO `.select()`.
  //
  // This is not a style choice — `.insert({...}).select('id')` makes EVERY
  // workspace creation fail, for every user, with:
  //   403 42501 "new row violates row-level security policy for table brand_kits"
  //
  // The INSERT policy is not what rejects it. `brand_kits_insert_own_account`
  // passes (verified against prod under `SET ROLE authenticated`: the same row
  // inserts fine with no RETURNING clause). What fails is the SELECT that
  // PostgREST appends for `select=id`, which is evaluated against
  // `brand_kits_select_visible` → `id IN (SELECT private.my_workspace_ids())`.
  // `my_workspace_ids()` SELF-JOINS `brand_kits`, and within the SAME
  // `INSERT ... RETURNING` statement that subquery cannot see the row being
  // inserted — so the row is invisible to its own RETURNING check even though
  // a plain SELECT finds it immediately afterwards.
  //
  // Supplying the uuid client-side removes the need to read anything back, so
  // no RETURNING is emitted and the policy is never asked an unanswerable
  // question. `brand_kits.id` has a DB default, so passing one is optional at
  // the schema level and authoritative here.
  const id = crypto.randomUUID();
  const browserZone = detectBrowserTimezone();

  const { error } = await client.from('brand_kits').insert({
    id,
    user_id: ownerId,
    account_id: accountId,
    name: trimmed && trimmed.length > 0 ? trimmed : 'New brand kit',
    is_default: existingCount === 0,
    ...(browserZone ? { timezone: browserZone } : {}),
    ...(parentBrandKitId ? { parent_brand_kit_id: parentBrandKitId } : {}),
  });

  if (error) throw new Error(error.message);
  return id;
}

/**
 * 🔴 Writes the ONLY signal that permits a brand name to be rendered into a
 * generated image.
 *
 * `brand_kits.name` — the field right below in `BrandKit` — is a RECORD LABEL.
 * `createBrandKit` writes `'New brand kit'` into it for every kit, and 8 of 13
 * production kits are placeholders ('My Brand Kit' ×4, 'Audit Test Value
 * 2026-05-29', two '(TEST)' rows). Until 2026-07-31 the image pipeline read that
 * label as the merchant's brand and authorised the model to print it, which
 * shipped a dripper engraved "Old Money — Mora" and a bag reading "New brand
 * kit". Presence of a label is not consent to render it.
 *
 * So consent is explicit and paired: the string the merchant confirmed, plus
 * the fact that they confirmed it. `clearRenderBrandName` writes both back to
 * NULL, which returns the account to "every surface unmarked". The DB
 * constraint `brand_kits_render_brand_name_consent_paired` rejects a half-set
 * pair so no caller can create a permission by accident.
 */
export async function confirmRenderBrandName(
  client: BrandKitMutationClient,
  ownerId: string,
  kitId: string,
  brandName: string,
): Promise<void> {
  const cleaned = brandName.trim();
  if (isPlaceholderBrandName(cleaned)) {
    throw new Error(
      'That looks like a placeholder rather than a brand name. Enter the name exactly as it should appear on your products.',
    );
  }
  const { error } = await client
    .from('brand_kits')
    .update({
      render_brand_name: cleaned,
      render_brand_name_confirmed_at: new Date().toISOString(),
    })
    .eq('id', kitId)
    .eq('user_id', ownerId);
  if (error) throw new Error(error.message);
}

export async function renameBrandKit(
  client: BrandKitMutationClient,
  ownerId: string,
  kitId: string,
  name: string,
): Promise<void> {
  const { error } = await client
    .from('brand_kits')
    .update({ name: name.trim() })
    .eq('id', kitId)
    .eq('user_id', ownerId);
  if (error) throw new Error(error.message);
}

/** Revokes consent — both columns back to NULL, surfaces unmarked again. */
export async function clearRenderBrandName(
  client: BrandKitMutationClient,
  ownerId: string,
  kitId: string,
): Promise<void> {
  const { error } = await client
    .from('brand_kits')
    .update({ render_brand_name: null, render_brand_name_confirmed_at: null })
    .eq('id', kitId)
    .eq('user_id', ownerId);
  if (error) throw new Error(error.message);
}

/**
 * ⚠️ NEWLY-ADDED COLUMNS ARE DECLARED OPTIONAL, DELIBERATELY.
 *
 * `rowToBrandKit` always supplies them, so a kit read through the app has them.
 * They are `?` because a hand-written FIXTURE that omits a column it does not
 * care about is legitimate — and demanding every column is precisely what made
 * this type unusable in tests, which is why a second, looser `BrandKit` was
 * written in `src/types/brand-kit.ts` in the first place. Requiring three new
 * fields here broke 20 fixtures across 14 files in a single commit; that is the
 * pressure that created the duplication the founder keeps hitting.
 *
 * The drift is now prevented at the other end instead: `src/types/brand-kit.ts`
 * derives its shape from the generated row, so no hand-written field list can
 * fall behind the schema again.
 */
export interface BrandKit {
  /** Seeded sample workspace (brand_kits.is_sample); Insights opens on 90 days for it. */
  is_sample?: boolean | null;
  id: string;
  user_id: string;
  /**
   * ⚠️ A RECORD LABEL, not a brand. Safe to show in a kit switcher; NEVER a
   * source of truth for what may be printed inside an image — that is
   * `render_brand_name` below, and only when `render_brand_name_confirmed_at`
   * is also set.
   */
  name: string;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  color_4: string | null;
  color_5: string | null;
  font_primary: string | null;
  font_secondary: string | null;
  logo_url: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  /**
   * The client's canonical website. Prefills a paid ad destination and a
   * calendar moment's URL. Never itself a permission to send traffic anywhere —
   * that is `owned_domains`.
   */
  website_url?: string | null;
  /**
   * Every bare host this brand legitimately owns, no scheme and no path.
   *
   * 🔴 FEEDS `AdReadinessContext.brandDomains`, WHICH HAD NO PRODUCER AT ALL.
   * `ad-readiness.ts` has always checked whether an ad "sends clicks to a
   * domain this brand does not own", and nothing in `src/` ever supplied the
   * list — `buildMetaDraftReviewProjection`'s `adReadinessContext` option
   * defaulted to `{}` and no caller passed one — so `domains.length > 0` was
   * never true and the check could never fire.
   *
   * ⚠️ EMPTY MEANS UNKNOWN, NOT "OWNS NOTHING". `ad-readiness.ts` reads an
   * empty list as "we cannot check the destination" and must go on doing so.
   * A zero standing for an unknown is the one unrecoverable failure in paid.
   */
  owned_domains?: string[];
  /**
   * BI-5 (A2): the value home for the disclaimerText lock. Carried here because
   * this is now the ONE `BrandKit` declaration — `src/types/brand-kit.ts`
   * re-exports it — and this field was previously only on that file's separate
   * copy, read by `brand-lock-directive.ts` and `resolve-brand-kit-chain.ts`.
   * Dropping it while collapsing the two types would have silently broken the
   * image brand-lock path.
   */
  disclaimer_text?: string | null;
  /**
   * The workspace's own zone. Post analytics record posting hours in it
   * (migration 20260926005000), so a suggested time is built in this zone.
   */
  timezone?: string | null;
  brand_guidelines: Record<string, unknown> | null;
  guidelines_version: number;
  guidelines_updated_at: string | null;
  guidelines_generated_from: string[];
  guidelines_schema_version: number;
  is_active: boolean;
  is_default: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** Merchant-confirmed name that may be rendered into images. NULL = none. */
  render_brand_name: string | null;
  /** Proof of that confirmation. NULL (either column) = no consent. */
  render_brand_name_confirmed_at: string | null;
  /**
   * Franchise/enterprise hierarchy link. NULL for an independent workspace or
   * a corporate root; set for a location, pointing at its corporate parent.
   * The only reader is `inherit_brand_kit` (`src/lib/ai-agent/tools/
   * inherit-brand-kit.ts`), which walks exactly one hop up — see
   * `AddBrandDialog`'s root-normalization for why this is never a 3-level
   * chain.
   */
  parent_brand_kit_id: string | null;
  /**
   * What kind of business this workspace is — decides the /insights headline
   * row. Inferred from connected sources and the brand profile until a person
   * confirms it (`business_model_source = 'user_confirmed'`). See
   * `src/lib/analytics/business-model.ts`.
   */
  business_model?: BusinessModel | null;
  business_model_source?: 'inferred' | 'user_confirmed';
  business_model_confidence?: 'high' | 'medium' | 'low' | null;
  business_model_evidence?: string[];
}

export interface UseBrandKitsReturn {
  /**
   * Every active kit the signed-in user can READ — including a member's own
   * personal kits that sit beside the team's. Use for management lists. A
   * switcher must use `selectableKits`, never this.
   */
  kits: BrandKit[];
  /**
   * The kits a person may SWITCH INTO: `kits` narrowed by the same owner rule
   * `activeKit` is resolved with (`eligibleWorkspaces`). Empty while the account
   * owner is unresolved or its lookup failed — fail closed, so a member is never
   * offered a kit the resolver would ignore.
   */
  selectableKits: BrandKit[];
  /**
   * The name the active kit has consented to render, or `null` when the answer
   * is "print nothing" — which is the default for every kit ever created.
   */
  renderBrandName: string | null;
  /**
   * What to PRE-FILL a confirmation field with, and where it came from, so the
   * prompt can be honest ("We think your brand is Aera Studio — is that what
   * should appear on your products?"). Never a permission on its own: only
   * `confirmRenderName` grants one. `null` means we have no honest guess and
   * the field should start empty rather than seeded with a record label.
   */
  renderBrandNameCandidate: BrandNameCandidate | null;
  confirmRenderName: (kitId: string, brandName: string) => Promise<void>;
  clearRenderName: (kitId: string) => Promise<void>;
  isSavingRenderName: boolean;
  isLoading: boolean;
  loadFailed: boolean;
  reload: () => void;
  isFetched: boolean;
  activeKit: BrandKit | null;
  /**
   * True while the per-user active-workspace pointer query
   * (`['active-workspace-pointer', userId]`) has not yet resolved — distinct
   * from `isLoading`, which only tracks the kits list.
   *
   * `pickActiveWorkspace(kits, pointerId)` treats `undefined` (pointer still
   * pending) and `null` (pointer definitively absent) identically — both are
   * falsy, so it falls through to the account default either way. That is
   * correct once the pointer is KNOWN to be null, but wrong while it is
   * merely unresolved: on every cold load, `activeKit` briefly resolves to
   * the account default even when the real (not-yet-fetched) pointer names a
   * different workspace. Consumers that render workspace-scoped content
   * (sidebar Chat, ⌘K search) must gate on this to avoid a one-frame flash of
   * the wrong workspace's data.
   */
  isActiveKitPending: boolean;
  setActiveKit: (kitId: string) => void;
  /**
   * Same underlying switch as `setActiveKit`, but awaitable and silent on
   * failure — no toast, no cache-recovery beyond the pointer invalidation the
   * mutation always does. For a caller (like `AddBrandDialog`) that must know
   * whether the switch actually landed before doing anything else, so it can
   * present an honest, specific error instead of a generic one and skip any
   * step that assumes the switch succeeded. Rejects with the same error the
   * DB trigger raised.
   */
  switchActiveKit: (kitId: string) => Promise<void>;
  renameKit: (kitId: string, name: string) => Promise<Awaited<ReturnType<typeof renameBrandKit>>>;
  setDefaultKit: (kitId: string) => void;
  isSettingDefault: boolean;
  /**
   * Create a brand kit. Pass the user-supplied name — omitting it falls back to
   * the `'New brand kit'` placeholder, which is why no UI affordance may call
   * this without one (see AddBrandDialog). `parentBrandKitId`, when passed,
   * links the new row into the franchise/enterprise hierarchy (see
   * `createBrandKit`'s doc comment).
   */
  createKit: (name?: string, parentBrandKitId?: string) => Promise<string | undefined>;
  isCreatingKit: boolean;
  /** Soft-archive (`is_active = false`). There is no hard delete — see the mutation. */
  archiveKit: (kitId: string) => Promise<void>;
  isArchivingKit: boolean;
  /** False when this is the only workspace; the DB refuses archiving it. */
  canArchive: boolean;
}

function rowToBrandKit(row: BrandKitRow): BrandKit {
  return {
    id: row.id,
    user_id: row.user_id,
    name: row.name,
    primary_color: row.primary_color,
    secondary_color: row.secondary_color,
    accent_color: row.accent_color,
    color_4: row.color_4,
    color_5: row.color_5,
    font_primary: row.font_primary,
    font_secondary: row.font_secondary,
    logo_url: row.logo_url,
    logo_light_url: row.logo_light_url,
    logo_dark_url: row.logo_dark_url,
    disclaimer_text: row.disclaimer_text ?? null,
    website_url: row.website_url ?? null,
    timezone: row.timezone ?? null,
    /* `?? []` because the column is NOT NULL with a `{}` default, so a row read
       before the migration reached this client would otherwise be `undefined`.
       An empty list means UNKNOWN to ad-readiness, never "owns nothing". */
    owned_domains: row.owned_domains ?? [],
    brand_guidelines:
      row.brand_guidelines &&
      typeof row.brand_guidelines === 'object' &&
      !Array.isArray(row.brand_guidelines)
        ? (row.brand_guidelines as Record<string, unknown>)
        : null,
    guidelines_version: row.guidelines_version,
    guidelines_updated_at: row.guidelines_updated_at,
    guidelines_generated_from: row.guidelines_generated_from ?? [],
    guidelines_schema_version: row.guidelines_schema_version,
    is_active: row.is_active,
    is_default: row.is_default,
    created_at: row.created_at,
    updated_at: row.updated_at,
    render_brand_name: row.render_brand_name,
    render_brand_name_confirmed_at: row.render_brand_name_confirmed_at,
    parent_brand_kit_id: row.parent_brand_kit_id,
    business_model: parseBusinessModel(row.business_model),
    business_model_source:
      row.business_model_source === 'user_confirmed' ? 'user_confirmed' : 'inferred',
    business_model_confidence:
      row.business_model_confidence === 'high' ||
      row.business_model_confidence === 'medium' ||
      row.business_model_confidence === 'low'
        ? row.business_model_confidence
        : null,
    business_model_evidence: row.business_model_evidence ?? [],
  };
}

export function useBrandKits(): UseBrandKitsReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const settingsCtx = useOptionalCreateSettings();
  const settings = settingsCtx?.settings;
  const updateSetting = settingsCtx?.updateSetting;
  const queryClient = useQueryClient();

  const query = useQuery<BrandKit[]>({
    queryKey: queryKeys.brandKits.list(userId ?? undefined),
    enabled: Boolean(userId),
    retry: 1,
    retryDelay: 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_kits')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('updated_at', { ascending: false })
        .retry(false);
      if (error) throw error;
      return (data ?? []).map(rowToBrandKit);
    },
  });

  const kits = useMemo(() => query.data ?? [], [query.data]);

  /**
   * This USER's active-workspace pointer. USER-scoped, so the raw session id is
   * correct — see the note on `companyQuery` below.
   */
  const pointerQuery = useQuery<string | null>({
    queryKey: queryKeys.brandKits.activePointer(userId ?? undefined),
    enabled: Boolean(userId),
    queryFn: () => readActiveWorkspacePointer(userId!),
    // 🔴 NEVER SERVE THIS STALE, and never take it from the persisted cache.
    //
    // The QueryClient default is `staleTime: 5 minutes` and this app persists
    // the cache to IndexedDB for 24h. Caught in live verification: switch
    // workspace, reload, and the sidebar rendered the PREVIOUS workspace —
    // React Query restored the pre-switch pointer from IndexedDB, considered it
    // fresh, and never refetched. Every other stale query costs a moment of old
    // data; a stale pointer misstates WHICH BRAND YOU ARE ACTING AS, and every
    // surface downstream inherits that lie.
    //
    // It is one uuid on one row, so refetching it on every mount is free.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  /**
   * Which workspace this user is acting in.
   *
   * ⚠️ THIS CHANGED 2026-08-22, and the previous comment here was the bug.
   * It read: "`brand_kits.is_default` (DB) is the SINGLE authority for the
   * active kit (spec 2026-07-17 §5, D6/D20)." That was true, and it was the
   * defect — `brand_kits` is ACCOUNT-scoped and
   * `uq_brand_kits_one_default_per_user` is `(user_id) WHERE is_default`, so
   * one account had exactly one active kit and **a teammate switching switched
   * it for everyone on the account, live**.
   *
   * Authority is now `user_profiles.active_brand_kit_id`, which is per-user.
   * `is_default` keeps a real job — the ACCOUNT's default, inherited by anyone
   * who has never switched — so team members still land on the owner's brand on
   * first login, exactly as they did before.
   *
   * The ordering rule is `pickActiveWorkspace`, which mirrors the SQL in
   * `resolve_effective_brand_kit_id()`. Do not inline the rule here.
   */
  const ownerQuery = useResolvedOwnerId();
  const ownerId = ownerQuery.data ?? null;
  const isOwnerPending = Boolean(userId) && ownerQuery.isPending;

  // 🔴 FAIL CLOSED, like `resolveOwnerId` itself. `pickActiveWorkspace` applies
  // its owner filter only when handed an owner id, so calling it with
  // `undefined` while the lookup is pending (or after it errored) is the
  // UNFILTERED pick — exactly the one that resolves a member to their personal
  // kit. No owner id means no active kit: null while pending, null on error.
  const activeKit = useMemo<BrandKit | null>(
    () => (ownerId ? pickActiveWorkspace(kits, pointerQuery.data, ownerId) : null),
    [kits, pointerQuery.data, ownerId],
  );
  const selectableKits = useMemo<BrandKit[]>(
    () => (ownerId ? eligibleWorkspaces(kits, ownerId) : []),
    [kits, ownerId],
  );

  const ownerLookupError = ownerQuery.isError ? ownerQuery.error : null;
  useEffect(() => {
    if (!ownerLookupError) return;
    logger.error(
      'useBrandKits: account owner lookup failed; no active workspace resolved',
      ownerLookupError,
    );
  }, [ownerLookupError]);

  // Reconcile the cache hint to the resolved workspace once kits are known: if
  // the cached id is absent from the user's kits, or disagrees with the
  // pointer, the server wins and the cache is rewritten to match. This never
  // overrides `activeKit` (already server-derived above) — it only keeps the
  // hint honest so a later read (e.g. before this hook mounts again) isn't
  // stale. Skipped while the pointer or owner is still pending so a half-known
  // pick can never be written into the hint.
  const isPointerPending = pointerQuery.isPending;
  useEffect(() => {
    if (isPointerPending || isOwnerPending) return;
    if (!activeKit) return;
    if (settings?.activeBrandKitId !== activeKit.id) {
      updateSetting?.('activeBrandKitId', activeKit.id);
      writeStoredActiveBrandKitId(activeKit.id);
    }
  }, [activeKit, isPointerPending, isOwnerPending, settings?.activeBrandKitId, updateSetting]);

  const brandKitsQueryKey = queryKeys.brandKits.list(userId ?? undefined);

  const setDefaultMutation = useMutation({
    mutationFn: async (kitId: string) => {
      const ownerId = await resolveOwnerId(userId!);
      return setDefaultBrandKit(supabase as unknown as BrandKitMutationClient, ownerId, kitId);
    },
    onMutate: async (kitId: string) => {
      await queryClient.cancelQueries({ queryKey: brandKitsQueryKey });
      const previous = queryClient.getQueryData<BrandKit[]>(brandKitsQueryKey);
      if (previous) {
        queryClient.setQueryData<BrandKit[]>(
          brandKitsQueryKey,
          previous.map((k) => ({ ...k, is_default: k.id === kitId })),
        );
      }
      return { previous };
    },
    onError: (err, _kitId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(brandKitsQueryKey, context.previous);
      }
      const message = err instanceof Error ? err.message : 'Failed to set default brand kit';
      showErrorToast('Could not set default', message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: brandKitsQueryKey });
    },
  });

  const switchMutation = useMutation({
    mutationFn: (kitId: string) =>
      switchWorkspace({ userId: userId!, brandKitId: kitId, queryClient }),
    // Recovery only, no toast here — `setActiveKit` below toasts for its own
    // fire-and-forget callers; `switchActiveKit` rejects and leaves the
    // message to whichever caller awaited it, so a switch requested from
    // inside another flow (e.g. right after creating the workspace) doesn't
    // show a generic "could not switch" toast alongside that flow's own,
    // more specific one.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.brandKits.activePointer(userId ?? undefined),
      });
    },
  });

  /**
   * Switch which workspace this user is acting in.
   *
   * ⚠️ THIS NO LONGER WRITES `is_default`, and that is the entire point. It
   * used to, which meant switching mutated an ACCOUNT-scoped data row and moved
   * every teammate with it. It now writes this user's own pointer and then
   * clears everything the previous workspace left behind — see
   * `switchWorkspace`, whose step order is load-bearing.
   *
   * No optimistic update: a switch that the DB trigger rejects (archived or
   * foreign kit) must not paint a workspace the user is not in. The pointer
   * query refetches and the sidebar follows it.
   */
  const setActiveKit = useCallback(
    (kitId: string) => {
      if (!userId) return;
      // localStorage stays a pure cache hint for pre-mount reads. It is written
      // AFTER the server write in the mutation's success path, never before —
      // writing it here would leave a rejected switch cached as if it happened.
      switchMutation.mutate(kitId, {
        onSuccess: () => {
          updateSetting?.('activeBrandKitId', kitId);
          writeStoredActiveBrandKitId(kitId);
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Failed to switch workspace';
          showErrorToast('Could not switch workspace', message);
        },
      });
    },
    [userId, updateSetting, switchMutation],
  );

  const switchActiveKit = useCallback(
    async (kitId: string) => {
      if (!userId) throw new Error('Not signed in');
      await switchMutation.mutateAsync(kitId);
      updateSetting?.('activeBrandKitId', kitId);
      writeStoredActiveBrandKitId(kitId);
    },
    [userId, updateSetting, switchMutation],
  );

  // `user_profiles` is USER-scoped (the raw session id is correct here — see
  // `src/lib/identity/table-scope.generated.ts`), unlike `brand_kits` above
  // which is account-scoped and needs `resolveOwnerId`. One hook, two tables,
  // two different right answers.
  const companyQuery = useQuery<string | null>({
    queryKey: queryKeys.brandKits.companyName(userId ?? undefined),
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('company_name')
        .eq('id', userId!)
        .maybeSingle();
      if (error) return null;
      return data?.company_name ?? null;
    },
  });

  const renderBrandName = useMemo(
    () =>
      resolveRenderBrandName({
        renderBrandName: activeKit?.render_brand_name ?? null,
        renderBrandNameConfirmedAt: activeKit?.render_brand_name_confirmed_at ?? null,
      }).permittedBrandName,
    [activeKit?.render_brand_name, activeKit?.render_brand_name_confirmed_at],
  );

  const renderBrandNameCandidate = useMemo(
    () =>
      pickBrandNameCandidate({
        brandGuidelines: activeKit?.brand_guidelines ?? null,
        companyName: companyQuery.data ?? null,
        brandKitName: activeKit?.name ?? null,
      }),
    [activeKit?.brand_guidelines, activeKit?.name, companyQuery.data],
  );

  const renderNameMutation = useMutation({
    mutationFn: async (vars: { kitId: string; brandName: string | null }) => {
      const ownerId = await resolveOwnerId(userId!);
      const client = supabase as unknown as BrandKitMutationClient;
      return vars.brandName === null
        ? clearRenderBrandName(client, ownerId, vars.kitId)
        : confirmRenderBrandName(client, ownerId, vars.kitId, vars.brandName);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to save brand name';
      showErrorToast('Could not save brand name', message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brandKitsQueryKey });
    },
  });

  /**
   * Archive a workspace. Every guard lives in `archive_brand_kit()` in the DB,
   * not here: it refuses the last remaining workspace, releases every user
   * pointing at the archived one, and promotes a new account default if the
   * archived kit was it. Putting those in the client would leave them
   * bypassable by any other caller — and this is exactly the class of rule that
   * belongs in code rather than prose.
   *
   * There is no hard delete. `brand_kits` is referenced by 13 tables, several
   * holding real content; archiving is reversible, deleting is not.
   */
  const archiveMutation = useMutation({
    mutationFn: async (kitId: string) => {
      const { error } = await supabase.rpc('archive_brand_kit', { p_kit_id: kitId });
      if (error) throw new Error(archiveRpcErrorMessage(error));
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to archive workspace';
      showErrorToast('Could not archive', message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brandKitsQueryKey });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.brandKits.activePointer(userId ?? undefined),
      });
    },
  });

  const renameMutation = useMutation({
    mutationFn: async (vars: { kitId: string; name: string }) => {
      const ownerId = await resolveOwnerId(userId!);
      return renameBrandKit(
        supabase as unknown as BrandKitMutationClient,
        ownerId,
        vars.kitId,
        vars.name,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brandKitsQueryKey });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (vars: { name?: string; parentBrandKitId?: string }) => {
      const ownerId = await resolveOwnerId(userId!);
      // Workspace-tenancy phase5a (2026-08-23): brand_kits INSERT is gated on
      // `account_id`, not `user_id` alone — see createBrandKit's doc comment.
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('id')
        .eq('owner_user_id', ownerId)
        .maybeSingle();
      if (accountError) throw new Error(`Account lookup failed: ${accountError.message}`);
      if (!account) throw new Error('Could not resolve the account for this workspace');
      return createBrandKit(
        supabase as unknown as BrandKitMutationClient,
        ownerId,
        kits.length,
        vars.name,
        account.id,
        vars.parentBrandKitId,
      );
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to create brand kit';
      showErrorToast('Could not create brand kit', message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brandKitsQueryKey });
    },
  });

  return {
    kits,
    selectableKits,
    renderBrandName,
    renderBrandNameCandidate,
    confirmRenderName: (kitId: string, brandName: string) =>
      renderNameMutation.mutateAsync({ kitId, brandName }),
    clearRenderName: (kitId: string) => renderNameMutation.mutateAsync({ kitId, brandName: null }),
    isSavingRenderName: renderNameMutation.isPending,
    isLoading: query.isLoading,
    loadFailed: query.isError && !query.data,
    reload: () => void query.refetch(),
    isFetched: query.isFetched,
    activeKit,
    isActiveKitPending: pointerQuery.isPending || isOwnerPending,
    setActiveKit,
    switchActiveKit,
    setDefaultKit: (kitId: string) => setDefaultMutation.mutate(kitId),
    isSettingDefault: setDefaultMutation.isPending,
    createKit: (name?: string, parentBrandKitId?: string) =>
      createMutation.mutateAsync({ name, parentBrandKitId }),
    renameKit: (kitId: string, name: string) => renameMutation.mutateAsync({ kitId, name }),
    isCreatingKit: createMutation.isPending,
    archiveKit: (kitId: string) => archiveMutation.mutateAsync(kitId),
    isArchivingKit: archiveMutation.isPending,
    /** True when archiving is impossible because this is the only workspace. */
    canArchive: kits.length > 1,
  };
}
